import Phaser from 'phaser'
import { Player } from './Player'
import { CreatureFactory } from './CreatureFactory'
import {
  AIState,
  CreatureType,
  CreatureDefinition,
  EVENTS,
  DamageEvent
} from '../types/GameTypes'

export class Creature extends Phaser.Physics.Arcade.Sprite {
  public readonly definition: CreatureDefinition
  public readonly creatureType: CreatureType

  // Combat stats
  private currentHp: number
  private lastAttackTime: number = 0

  // AI state
  private aiState: AIState = 'idle'
  private stateTimer: number = 0
  private wanderDirection: { x: number; y: number } = { x: 0, y: 0 }

  // Spawn position (for returning)
  private spawnX: number
  private spawnY: number
  private maxChaseDistance: number = 300 // Max distance from spawn before returning

  // Target reference
  private target: Player | null = null

  // Health bar
  private healthBar: Phaser.GameObjects.Graphics | null = null

  // Death callback for respawn system
  public onDeath: (() => void) | null = null

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    type: CreatureType,
    target: Player
  ) {
    super(scene, x, y, `creature-${type}`, 0)

    this.creatureType = type
    this.definition = CreatureFactory.getDefinition(type)
    this.currentHp = this.definition.maxHp
    this.target = target

    // Store spawn position
    this.spawnX = x
    this.spawnY = y

    // Add to scene and enable physics
    scene.add.existing(this as unknown as Phaser.GameObjects.GameObject)
    scene.physics.add.existing(this as unknown as Phaser.GameObjects.GameObject)

    // Set up physics body
    const body = this.body as Phaser.Physics.Arcade.Body
    body.setSize(24, 24)
    body.setOffset(4, 8)
    this.setCollideWorldBounds(true)

    // Set initial depth
    this.setDepth(this.y)

    // Create animations
    this.createAnimations()

    // Create health bar
    this.createHealthBar()

    // Start in idle state
    this.setAIState('idle')
  }

  private createAnimations(): void {
    const anims = this.scene.anims
    const key = `creature-${this.creatureType}`

    // Only create if they don't exist
    if (!anims.exists(`${key}-walk-down`)) {
      anims.create({
        key: `${key}-walk-down`,
        frames: anims.generateFrameNumbers(key, { start: 0, end: 3 }),
        frameRate: 8,
        repeat: -1
      })

      anims.create({
        key: `${key}-walk-up`,
        frames: anims.generateFrameNumbers(key, { start: 4, end: 7 }),
        frameRate: 8,
        repeat: -1
      })

      anims.create({
        key: `${key}-walk-left`,
        frames: anims.generateFrameNumbers(key, { start: 8, end: 11 }),
        frameRate: 8,
        repeat: -1
      })

      anims.create({
        key: `${key}-walk-right`,
        frames: anims.generateFrameNumbers(key, { start: 12, end: 15 }),
        frameRate: 8,
        repeat: -1
      })

      anims.create({
        key: `${key}-idle`,
        frames: [{ key: key, frame: 0 }],
        frameRate: 1
      })
    }
  }

  private createHealthBar(): void {
    this.healthBar = this.scene.add.graphics()
    this.updateHealthBar()
  }

  private updateHealthBar(): void {
    if (!this.healthBar) return

    this.healthBar.clear()

    // Only show health bar if creature is damaged
    if (this.currentHp < this.definition.maxHp) {
      const barWidth = 28
      const barHeight = 4
      const x = this.x - barWidth / 2
      const y = this.y - 20

      // Background
      this.healthBar.fillStyle(0x333333, 0.8)
      this.healthBar.fillRect(x, y, barWidth, barHeight)

      // Health fill
      const healthPercent = this.currentHp / this.definition.maxHp
      const fillColor = healthPercent > 0.5 ? 0x22c55e : healthPercent > 0.25 ? 0xfbbf24 : 0xef4444
      this.healthBar.fillStyle(fillColor, 1)
      this.healthBar.fillRect(x, y, barWidth * healthPercent, barHeight)

      // Border
      this.healthBar.lineStyle(1, 0x000000, 0.5)
      this.healthBar.strokeRect(x, y, barWidth, barHeight)
    }
  }

  private setAIState(newState: AIState): void {
    this.aiState = newState
    this.stateTimer = 0

    switch (newState) {
      case 'idle':
        this.setVelocity(0, 0)
        this.anims.play(`creature-${this.creatureType}-idle`, true)
        break

      case 'wandering':
        // Pick random direction
        const angle = Math.random() * Math.PI * 2
        this.wanderDirection = {
          x: Math.cos(angle),
          y: Math.sin(angle)
        }
        break

      case 'chasing':
      case 'attacking':
      case 'returning':
        // Will be handled in update
        break

      case 'dead':
        this.setVelocity(0, 0)
        this.setTint(0x666666)
        this.setAlpha(0.7)
        const body = this.body as Phaser.Physics.Arcade.Body
        body.enable = false
        if (this.healthBar) {
          this.healthBar.clear()
        }
        break
    }
  }

  public update(delta: number): void {
    if (this.aiState === 'dead') return

    this.stateTimer += delta

    // Update depth for Y-sorting
    this.setDepth(this.y)

    // Update health bar position
    this.updateHealthBar()

    switch (this.aiState) {
      case 'idle':
        this.updateIdle()
        break
      case 'wandering':
        this.updateWandering(delta)
        break
      case 'chasing':
        this.updateChasing()
        break
      case 'attacking':
        this.updateAttacking()
        break
      case 'returning':
        this.updateReturning()
        break
    }
  }

  private updateIdle(): void {
    // Check for player in aggro range
    if (this.target && !this.target.isDead) {
      const distToPlayer = this.distanceTo(this.target.x, this.target.y)
      if (distToPlayer <= this.definition.aggroRange) {
        this.setAIState('chasing')
        return
      }
    }

    // After some time, start wandering
    if (this.stateTimer > 2000 + Math.random() * 3000) {
      this.setAIState('wandering')
    }
  }

  private updateWandering(_delta: number): void {
    // Check for player in aggro range
    if (this.target && !this.target.isDead) {
      const distToPlayer = this.distanceTo(this.target.x, this.target.y)
      if (distToPlayer <= this.definition.aggroRange) {
        this.setAIState('chasing')
        return
      }
    }

    // Move in wander direction
    const speed = this.definition.speed * 0.5 // Slower when wandering
    this.setVelocity(
      this.wanderDirection.x * speed,
      this.wanderDirection.y * speed
    )

    // Update animation based on direction
    this.updateMoveAnimation(this.wanderDirection.x, this.wanderDirection.y)

    // Check if too far from spawn
    const distFromSpawn = this.distanceTo(this.spawnX, this.spawnY)
    if (distFromSpawn > 100) {
      // Head back toward spawn
      this.wanderDirection = {
        x: (this.spawnX - this.x) / distFromSpawn,
        y: (this.spawnY - this.y) / distFromSpawn
      }
    }

    // Stop wandering after some time
    if (this.stateTimer > 1500 + Math.random() * 2000) {
      this.setAIState('idle')
    }
  }

  private updateChasing(): void {
    if (!this.target || this.target.isDead) {
      this.setAIState('returning')
      return
    }

    const distToPlayer = this.distanceTo(this.target.x, this.target.y)
    const distFromSpawn = this.distanceTo(this.spawnX, this.spawnY)

    // Check if player escaped (too far from spawn)
    if (distFromSpawn > this.maxChaseDistance) {
      this.setAIState('returning')
      return
    }

    // Check if in attack range
    if (distToPlayer <= this.definition.attackRange) {
      this.setAIState('attacking')
      return
    }

    // Move toward player
    const dirX = (this.target.x - this.x) / distToPlayer
    const dirY = (this.target.y - this.y) / distToPlayer

    this.setVelocity(
      dirX * this.definition.speed,
      dirY * this.definition.speed
    )

    this.updateMoveAnimation(dirX, dirY)
  }

  private updateAttacking(): void {
    if (!this.target || this.target.isDead) {
      this.setAIState('returning')
      return
    }

    const distToPlayer = this.distanceTo(this.target.x, this.target.y)

    // If player moved out of range, chase
    if (distToPlayer > this.definition.attackRange + 10) {
      this.setAIState('chasing')
      return
    }

    // Stop moving while attacking
    this.setVelocity(0, 0)

    // Face the target
    const dirX = this.target.x - this.x
    const dirY = this.target.y - this.y
    this.updateMoveAnimation(dirX, dirY)

    // Attack on cooldown
    const now = this.scene.time.now
    if (now - this.lastAttackTime >= this.definition.attackSpeed) {
      this.performAttack()
      this.lastAttackTime = now
    }
  }

  private updateReturning(): void {
    const distFromSpawn = this.distanceTo(this.spawnX, this.spawnY)

    // Check if back at spawn
    if (distFromSpawn < 20) {
      // Heal when returning to spawn
      this.currentHp = this.definition.maxHp
      this.updateHealthBar()
      this.setAIState('idle')
      return
    }

    // Check if player came back into range
    if (this.target && !this.target.isDead) {
      const distToPlayer = this.distanceTo(this.target.x, this.target.y)
      if (distToPlayer <= this.definition.aggroRange) {
        this.setAIState('chasing')
        return
      }
    }

    // Move toward spawn
    const dirX = (this.spawnX - this.x) / distFromSpawn
    const dirY = (this.spawnY - this.y) / distFromSpawn

    this.setVelocity(
      dirX * this.definition.speed,
      dirY * this.definition.speed
    )

    this.updateMoveAnimation(dirX, dirY)
  }

  private updateMoveAnimation(dirX: number, dirY: number): void {
    const key = `creature-${this.creatureType}`

    if (Math.abs(dirX) > Math.abs(dirY)) {
      this.anims.play(`${key}-walk-${dirX > 0 ? 'right' : 'left'}`, true)
    } else {
      this.anims.play(`${key}-walk-${dirY > 0 ? 'down' : 'up'}`, true)
    }
  }

  private performAttack(): void {
    if (!this.target || this.target.isDead) return

    // Deal damage to player
    this.target.takeDamage(this.definition.attack, this.creatureType)

    // Visual attack effect
    this.setTint(0xff6666)
    this.scene.time.delayedCall(100, () => {
      if (this.aiState !== 'dead') {
        this.clearTint()
      }
    })
  }

  /**
   * Take damage from an attack
   */
  public takeDamage(damage: number, isCritical: boolean = false): number {
    if (this.aiState === 'dead') return 0

    // Apply defense
    const actualDamage = Math.max(1, damage - this.definition.defense + Phaser.Math.Between(-2, 2))
    const finalDamage = isCritical ? Math.floor(actualDamage * 1.5) : actualDamage

    this.currentHp = Math.max(0, this.currentHp - finalDamage)

    // Emit damage event
    const damageEvent: DamageEvent = {
      attacker: 'player',
      target: this.creatureType,
      damage: finalDamage,
      isCritical,
      targetX: this.x,
      targetY: this.y
    }
    this.scene.events.emit(EVENTS.DAMAGE_DEALT, damageEvent)

    // Flash red when hit
    this.setTint(0xff0000)
    this.scene.time.delayedCall(100, () => {
      if (this.aiState !== 'dead') {
        this.clearTint()
      }
    })

    // Update health bar
    this.updateHealthBar()

    // Check for death
    if (this.currentHp <= 0) {
      this.die()
    } else if (this.aiState === 'idle' || this.aiState === 'wandering') {
      // Aggro when hit
      this.setAIState('chasing')
    }

    return finalDamage
  }

  /**
   * Handle creature death
   */
  private die(): void {
    this.setAIState('dead')

    // Emit death event
    this.scene.events.emit(EVENTS.CREATURE_DIED, {
      entity: this.creatureType,
      killedBy: 'player',
      x: this.x,
      y: this.y,
      experienceReward: this.definition.experienceReward
    })

    // Trigger death callback for respawn system
    if (this.onDeath) {
      this.onDeath()
    }
  }

  /**
   * Check if creature is alive
   */
  public isAlive(): boolean {
    return this.aiState !== 'dead'
  }

  /**
   * Get current HP
   */
  public getCurrentHp(): number {
    return this.currentHp
  }

  /**
   * Get max HP
   */
  public getMaxHp(): number {
    return this.definition.maxHp
  }

  /**
   * Get current AI state
   */
  public getAIState(): AIState {
    return this.aiState
  }

  /**
   * Distance to a point
   */
  private distanceTo(x: number, y: number): number {
    return Phaser.Math.Distance.Between(this.x, this.y, x, y)
  }

  /**
   * Clean up resources
   */
  public destroy(fromScene?: boolean): void {
    if (this.healthBar) {
      this.healthBar.destroy()
      this.healthBar = null
    }
    super.destroy(fromScene)
  }
}
